import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAppSelector } from '@/features/hooks';
import { useCurrency } from '@/hooks/useCurrency';
import { CurrencySelect } from '@/components/ui/currency-select';
import type { Bill } from '@/lib/types';

const EMOJI_OPTIONS = ['🏠', '⚡', '💧', '📶', '🚗', '💊', '📱', '🎓', '🏥', '🛡️', '📰', '🎵', '🏋️', '🍕', '💳'];

const billSchema = z.object({
  name: z.string().min(1, 'Bill name is required').max(100),
  amount: z.coerce.number().positive('Amount must be positive'),
  dueDate: z.string().min(1, 'Due date is required'),
  category: z.string().optional().or(z.literal('')),
  account: z.string().optional().or(z.literal('')),
  currency: z.string().length(3, 'Use a 3-letter currency code'),
  isRecurring: z.boolean().default(false),
  recurringFrequency: z.enum(['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly']).optional(),
  recurringInterval: z.coerce.number().min(1).optional().or(z.literal('')).transform((v) => (v === '' ? undefined : v)),
  emoji: z.string().optional(),
  notes: z.string().max(500).optional().or(z.literal('')),
  reminderDays: z.coerce.number().min(0).default(3),
});

export type BillFormValues = z.infer<typeof billSchema>;

interface BillFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: BillFormValues) => Promise<void>;
  bill?: Bill | null;
  isSubmitting?: boolean;
}

export function BillForm({ open, onOpenChange, onSubmit, bill, isSubmitting }: BillFormProps) {
  const isEditing = !!bill;
  const { categories } = useAppSelector((state) => state.categories);
  const { accounts } = useAppSelector((state) => state.accounts);
  const { currency: userCurrency } = useCurrency();

  const expenseCategories = categories.filter((c) => c.type === 'expense');

  const form = useForm<BillFormValues>({
    resolver: zodResolver(billSchema),
    defaultValues: {
      name: '',
      amount: 0,
      dueDate: new Date().toISOString().split('T')[0],
      category: '',
      account: '',
      currency: userCurrency,
      isRecurring: false,
      recurringFrequency: undefined,
      recurringInterval: undefined,
      emoji: '',
      notes: '',
      reminderDays: 3,
    },
  });

  useEffect(() => {
    if (bill) {
      form.reset({
        name: bill.name,
        amount: bill.amount,
        dueDate: bill.dueDate ? bill.dueDate.split('T')[0] : '',
        category: bill.category || '',
        account: (typeof bill.account === 'object' && bill.account !== null ? (bill.account as { _id: string })._id : bill.account) || '',
        currency: bill.currency || userCurrency,
        isRecurring: bill.isRecurring,
        recurringFrequency: bill.recurringPattern?.frequency,
        recurringInterval: bill.recurringPattern?.interval,
        emoji: bill.emoji || '',
        notes: bill.notes || '',
        reminderDays: bill.reminderDays ?? 3,
      });
    } else {
      form.reset({
        name: '',
        amount: 0,
        dueDate: new Date().toISOString().split('T')[0],
        category: '',
        account: '',
        currency: userCurrency,
        isRecurring: false,
        recurringFrequency: undefined,
        recurringInterval: undefined,
        emoji: '',
        notes: '',
        reminderDays: 3,
      });
    }
  }, [bill, form, open, userCurrency]);

  const handleSubmit = async (values: BillFormValues) => {
    await onSubmit(values);
    form.reset();
  };

  const watchIsRecurring = form.watch('isRecurring');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Bill' : 'Add Bill'}</SheetTitle>
          <SheetDescription>
            {isEditing ? 'Update bill details.' : 'Add a new bill to track.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emoji<OptionalTag /></FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-1.5">
                      {EMOJI_OPTIONS.map((e) => (
                        <button
                          key={e}
                          type="button"
                          className={`h-9 w-9 rounded-md border text-lg flex items-center justify-center transition-colors ${
                            field.value === e
                              ? 'border-primary bg-primary/10'
                              : 'border-input hover:bg-accent'
                          }`}
                          onClick={() => field.onChange(field.value === e ? '' : e)}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name<RequiredAsterisk /></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Rent, Electric Bill" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount<RequiredAsterisk /></FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="0.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date<RequiredAsterisk /></FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency<RequiredAsterisk /></FormLabel>
                    <FormControl>
                      <CurrencySelect
                        value={field.value || userCurrency}
                        onChange={field.onChange}
                        required
                        testId="bill-currency-select"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category<OptionalTag /></FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {expenseCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="account"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account<OptionalTag /></FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accounts.filter(a => a.isActive).map((acc) => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.emoji ? `${acc.emoji} ` : ''}{acc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isRecurring"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="text-sm font-normal">Recurring bill</FormLabel>
                </FormItem>
              )}
            />

            {watchIsRecurring && (
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="recurringFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Frequency<RequiredAsterisk /></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ''}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Biweekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recurringInterval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interval<OptionalTag /></FormLabel>
                      <FormControl>
                        <Input type="number" min="1" placeholder="1" {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="reminderDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reminder Days<OptionalTag /></FormLabel>
                  <FormControl>
                    <Input type="number" min="0" placeholder="3" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes<OptionalTag /></FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional notes..." rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Bill'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
